// src/components/Popup/RecWelcomePopup.js

import React, { useEffect, useState } from 'react';
import Popup from './Popup';

/**
 * Welcome popup for recommendation page
 * Shows congratulatory message and key information about the recommendations
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the popup is open
 * @param {Function} props.onClose - Function to call when popup is closed
 * @param {number} props.optionCount - Number of clinic options found
 * @param {string[]} props.toggles - Array of description strings for each active toggle
 */
const RecWelcomePopup = ({
  isOpen,
  onClose,
  optionCount = 3,
  toggles = []
}) => {
  // State to control staggered visibility of items
  const [visibleToggles, setVisibleToggles] = useState(
    Array(toggles.length).fill(false)
  );
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset when closed
      setVisibleToggles(Array(toggles.length).fill(false));
      setShowButton(false);
      return;
    }

    // Staggered reveal of toggles
    const toggleTimers = toggles.map((_, i) =>
      setTimeout(() => {
        setVisibleToggles(prev => {
          const copy = [...prev];
          copy[i] = true;
          return copy;
        });
      }, 300 + i * 300) // start after 0.3s, then every 0.3s
    );

    // Reveal button after all toggles
    const buttonTimer = setTimeout(
      () => setShowButton(true),
      300 + toggles.length * 300 + 300
    );

    // Cleanup on unmount or close
    return () => {
      toggleTimers.forEach(clearTimeout);
      clearTimeout(buttonTimer);
    };
  }, [isOpen, toggles]);

  const CheckmarkIcon = () => (
    <svg
      className="w-7 h-7 text-gray-600 flex-shrink-0 mr-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );

  return (
    <Popup isOpen={isOpen} onClose={onClose}>
      {/* container is full-width on mobile, capped on desktop */}
      <div className="w-full  mx-auto py-12 md:py-10 px-6 md:px-10">
        {/* Always rendered title */}
        <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-4 text-center transition-colors duration-200 opacity-100">
          We found {optionCount} clinics{" "}
          <span className="block md:inline underline bg-gradient-to-r from-blue-800 to-blue-600 bg-clip-text text-transparent md:ml-1">
            great for you
          </span>
        </h2>

        {/* Separator */}
        {toggles.length > 0 && (
          <div className="border-t mt-6 border-gray-300 my-4 opacity-100" />
        )}

{toggles.map((desc, i) => (
  <div
    key={desc}
    className={`flex bg-blue-100 max-w-max pr-6 pl-3 rounded-2xl p-2 mb-3 transition-opacity duration-500
      ${visibleToggles[i] ? 'opacity-100' : 'opacity-0'}
      mx-auto`} // Center on mobile, align left on md+
  >
    <CheckmarkIcon />
    <p className="text-lg text-gray-600">{desc}</p>
  </div>
))}


        {/* Continue button */}
        <div className={`mt-5 flex justify-center transition-opacity duration-500 ${
          showButton ? 'opacity-100' : 'opacity-0'
        }`}>
          <button
            onClick={onClose}
            className="text-2xl bg-mm_blue hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-2xl shadow-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          >
            Continue
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default RecWelcomePopup;